import React, { useState, useRef, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { X, Plus, Tag as TagIcon } from 'lucide-react';
import type { TagStats } from '@/types/WeeklyReport';

interface TemplateTagInputProps {
    tags: string[];
    onChange: (tags: string[]) => void;
    suggestedTags?: TagStats[];
    maxTags?: number;
    placeholder?: string;
    disabled?: boolean;
}

export const TemplateTagInput: React.FC<TemplateTagInputProps> = ({
    tags,
    onChange,
    suggestedTags = [],
    maxTags = 10,
    placeholder = 'Add a tag...',
    disabled = false
}) => {
    const [inputValue, setInputValue] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Close suggestions when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const sanitizeTag = (tag: string): string => {
        return tag.trim().toLowerCase().replace(/[^a-z0-9-]/g, '-').slice(0, 50);
    };

    const addTag = (tag: string) => {
        const sanitized = sanitizeTag(tag);
        if (sanitized && !tags.includes(sanitized) && tags.length < maxTags) {
            onChange([...tags, sanitized]);
            setInputValue('');
            setShowSuggestions(false);
        }
    };

    const removeTag = (tagToRemove: string) => {
        onChange(tags.filter(tag => tag !== tagToRemove));
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && inputValue) {
            e.preventDefault();
            addTag(inputValue);
        } else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
            removeTag(tags[tags.length - 1]);
        }
    };

    // Filter suggestions based on input
    const filteredSuggestions = suggestedTags
        .filter(s => 
            !tags.includes(s.tag) && 
            s.tag.includes(inputValue.toLowerCase())
        )
        .slice(0, 8);

    return (
        <div ref={containerRef} className="w-full space-y-2">
            {/* Tags display */}
            <div className="flex flex-wrap gap-2">
                {tags.map(tag => (
                    <Badge 
                        key={tag} 
                        variant="secondary" 
                        className="flex items-center gap-1 pr-1"
                    >
                        <TagIcon size={12} className="opacity-60" />
                        {tag}
                        {!disabled && (
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-4 w-4 p-0 ml-1 hover:bg-destructive/20"
                                onClick={() => removeTag(tag)}
                            >
                                <X size={12} />
                            </Button>
                        )}
                    </Badge>
                ))}
            </div>

            {/* Input */}
            {!disabled && tags.length < maxTags && (
                <div className="relative">
                    <Input
                        ref={inputRef}
                        type="text"
                        value={inputValue}
                        onChange={(e) => {
                            setInputValue(e.target.value);
                            setShowSuggestions(true);
                        }}
                        onFocus={() => setShowSuggestions(true)}
                        onKeyDown={handleKeyDown}
                        placeholder={placeholder}
                        className="pr-10"
                    />
                    {inputValue && (
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                            onClick={() => addTag(inputValue)}
                        >
                            <Plus size={16} />
                        </Button>
                    )}

                    {/* Suggestions dropdown */}
                    {showSuggestions && filteredSuggestions.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-md max-h-48 overflow-auto">
                            {filteredSuggestions.map(suggestion => (
                                <button
                                    key={suggestion.tag}
                                    type="button"
                                    className="w-full px-3 py-2 text-left text-sm hover:bg-accent flex items-center justify-between"
                                    onClick={() => addTag(suggestion.tag)}
                                >
                                    <span className="flex items-center gap-2">
                                        <TagIcon size={14} className="opacity-60" />
                                        {suggestion.tag}
                                    </span>
                                    <span className="text-xs text-muted-foreground">
                                        {suggestion.count} uses
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Limit indicator */}
            {tags.length >= maxTags && (
                <p className="text-xs text-muted-foreground">
                    Maximum {maxTags} tags reached
                </p>
            )}
        </div>
    );
};

export default TemplateTagInput;

